import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Lightbulb, ClipboardList, FileCheck2, MonitorSmartphone, Rocket, TrendingUp,
  ArrowRight, Star, Check, Handshake, Users, Landmark, Megaphone, LineChart,
  FileText, BookOpen, DollarSign, GraduationCap, Sparkles, MapPin, ExternalLink, RotateCcw,
} from "lucide-react";

const COLOR = {
  blue: { icon: "text-blue-400", ring: "border-blue-500/30", chip: "border-blue-500/40 bg-blue-500/10 text-blue-300", dot: "bg-blue-500", soft: "bg-blue-500/10 border-blue-500/20" },
  sky: { icon: "text-sky-400", ring: "border-sky-500/30", chip: "border-sky-500/40 bg-sky-500/10 text-sky-300", dot: "bg-sky-500", soft: "bg-sky-500/10 border-sky-500/20" },
  teal: { icon: "text-teal-400", ring: "border-teal-500/30", chip: "border-teal-500/40 bg-teal-500/10 text-teal-300", dot: "bg-teal-500", soft: "bg-teal-500/10 border-teal-500/20" },
  lime: { icon: "text-lime-400", ring: "border-lime-500/30", chip: "border-lime-500/40 bg-lime-500/10 text-lime-300", dot: "bg-lime-500", soft: "bg-lime-500/10 border-lime-500/20" },
  gold: { icon: "text-gold", ring: "border-gold/30", chip: "border-gold/40 bg-gold/10 text-gold", dot: "bg-gold", soft: "bg-gold/10 border-gold/20" },
  purple: { icon: "text-purple-400", ring: "border-purple-500/30", chip: "border-purple-500/40 bg-purple-500/10 text-purple-300", dot: "bg-purple-500", soft: "bg-purple-500/10 border-purple-500/20" },
};

const PHASES = [
  { key: "idea", num: "Phase 1", name: "Idea", color: "blue", icon: Lightbulb, helpIcon: Handshake, helpTo: "/start", helpCta: "Validate my idea",
    steps: [[1, "Develop Your Business Idea"], [2, "Research Your Market"], [3, "Choose Your Business Name"]],
    help: ["Business Idea Guidance", "Market Research Support", "Name & Branding Services"] },
  { key: "plan", num: "Phase 2", name: "Plan", color: "sky", icon: ClipboardList, helpIcon: Users, helpTo: "/business-plan", helpCta: "Build my plan",
    steps: [[4, "Choose a Business Structure"], [5, "Create Your Business Plan"]],
    help: ["Business Plan Writing", "Financial Projections", "Strategy & Consulting"] },
  { key: "establish", num: "Phase 3", name: "Establish", color: "teal", icon: FileCheck2, helpIcon: Landmark, helpTo: "/book", helpCta: "Get setup help",
    steps: [[6, "Register Your Business"], [7, "Get Your EIN"], [8, "Get Licenses & Permits"], [9, "Open a Business Bank Account"], [10, "Set Up Accounting & Taxes"]],
    help: ["Registration Guidance", "Accounting Setup", "Compliance Support"] },
  { key: "build", num: "Phase 4", name: "Build", color: "lime", icon: MonitorSmartphone, helpIcon: MonitorSmartphone, helpTo: "/services", helpCta: "See website packages",
    steps: [[11, "Build Your Brand"], [12, "Secure Your Online Identity"], [13, "Build Your Website"], [14, "Establish Your Local Presence"]],
    help: ["Branding & Design", "Website Development", "Google Business Setup"] },
  { key: "launch", num: "Phase 5", name: "Launch", color: "gold", icon: Rocket, helpIcon: Megaphone, helpTo: "/solutions", helpCta: "Plan my launch",
    steps: [[15, "Create Your Marketing Plan"], [16, "Prepare to Launch"], [17, "Launch Your Business"]],
    help: ["Marketing Strategy", "Pre-Launch Checklist", "Ongoing Marketing"] },
  { key: "grow", num: "Phase 6", name: "Grow", color: "purple", icon: TrendingUp, helpIcon: LineChart, helpTo: "/grow", helpCta: "Grow my business",
    steps: [[18, "Measure, Improve & Grow"]],
    help: ["Analytics & Tracking", "Optimization", "Growth Strategy"] },
];

const RESOURCES = [
  { icon: ClipboardList, title: "Checklists & Guides", to: "/marketplace" },
  { icon: BookOpen, title: "Business Guides", to: "/plan" },
  { icon: DollarSign, title: "Funding & Grants", to: "/business-plan" },
  { icon: GraduationCap, title: "Training & Workshops", to: "/book" },
  { icon: Sparkles, title: "AI Tools for Business", to: "/ai-concierge" },
];

const US_STATES = ["Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"];

const TOTAL_STEPS = 18;
const STORAGE_KEY = "atf_roadmap_progress";

export default function StartYourBusiness() {
  const [done, setDone] = useState([]);
  const [state, setState] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setDone(JSON.parse(raw));
    } catch (e) { /* ignore */ }
  }, []);

  const toggle = (n) => {
    setDone((prev) => {
      const next = prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n];
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };
  const reset = () => { setDone([]); try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ } };

  const pct = Math.round((done.length / TOTAL_STEPS) * 100);

  const stateSearch = (q) => `https://www.google.com/search?q=${encodeURIComponent(`${state} ${q} site:.gov`)}`;

  return (
    <div data-testid="start-business-page" className="bg-navy">
      <Helmet>
        <title>How to Start a Business — 18-Step Roadmap | ATF WebWorks</title>
        <meta name="description" content="Follow our free 6-phase, 18-step roadmap to start your business — from idea to launch and growth. Guides, checklists, funding links and official SBA resources, with expert help at every step." />
      </Helmet>

      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden grain pt-24">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 relative">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5">
                <span className="h-px w-8 bg-gold" /> From Idea to Impact
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05]">
                Start your business — <span className="text-gold">step by step.</span>
              </h1>
              <p className="text-base md:text-lg text-gray-300 font-light mt-6 max-w-xl leading-relaxed">
                Follow our step-by-step roadmap to turn your idea into a thriving business. Check off each step as you go, at your own pace — and lean on ATF WebWorks whenever you want a hand.
              </p>
            </motion.div>

            {/* Phase flow */}
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="flex flex-wrap items-center justify-start lg:justify-end gap-x-2 gap-y-4">
              {PHASES.map((p, i) => {
                const Icon = p.icon; const c = COLOR[p.color];
                return (
                  <div key={p.key} className="flex items-center gap-2">
                    <div className="flex flex-col items-center gap-2 w-20">
                      <div className={`w-14 h-14 rounded-full border ${c.ring} bg-white/[0.03] flex items-center justify-center`}>
                        <Icon size={22} className={c.icon} />
                      </div>
                      <span className="text-[11px] uppercase tracking-widest text-gray-400">{p.name}</span>
                    </div>
                    {i < PHASES.length - 1 && <ArrowRight size={16} className="text-gray-600 shrink-0 mb-6" />}
                  </div>
                );
              })}
            </motion.div>
          </div>

          {/* Assessment banner */}
          <div className="mt-12 bg-white/[0.04] border border-white/10 px-6 py-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-6" data-testid="sbp-assessment-banner">
            <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
              <Star size={20} className="text-gold" />
            </div>
            <div className="flex-1">
              <div className="text-white font-semibold">Not sure where to start?</div>
              <p className="text-sm text-gray-400">Take the Free Business Assessment and we'll help identify your next steps.</p>
            </div>
            <Link to="/start" data-testid="sbp-take-assessment"
              className="bg-gold text-navy hover:bg-gold-light transition-colors px-6 py-3 text-sm font-semibold text-center whitespace-nowrap">
              Take the Assessment
            </Link>
          </div>
        </div>
      </section>

      {/* Progress bar */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-white tracking-tight">The 18 steps to start your business</h2>
            <p className="text-sm text-gray-400 mt-1">Tick each step as you complete it — your progress is saved on this device.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-2xl font-heading text-gold leading-none" data-testid="sbp-progress-count">{done.length}<span className="text-gray-500 text-lg">/{TOTAL_STEPS}</span></div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500">complete</div>
            </div>
            {done.length > 0 && (
              <button onClick={reset} data-testid="sbp-reset"
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors border border-white/10 px-3 py-2">
                <RotateCcw size={13} /> Reset
              </button>
            )}
          </div>
        </div>
        <div className="h-2 bg-white/5 rounded-full overflow-hidden mb-12">
          <motion.div className="h-full bg-gold" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} data-testid="sbp-progress-bar" />
        </div>
      </section>

      {/* Phase cards */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PHASES.map((p, i) => {
            const Icon = p.icon; const HelpIcon = p.helpIcon; const c = COLOR[p.color];
            return (
              <motion.div key={p.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex flex-col bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors" data-testid={`sbp-phase-${p.key}`}>
                <div className="p-6 flex-1">
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-11 h-11 rounded-full border ${c.ring} bg-white/[0.03] flex items-center justify-center`}>
                      <Icon size={20} className={c.icon} />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-gray-500">{p.num}</div>
                      <div className="font-heading text-xl text-white tracking-tight">{p.name}</div>
                    </div>
                  </div>

                  <ul className="space-y-2.5">
                    {p.steps.map(([n, label]) => {
                      const isDone = done.includes(n);
                      return (
                        <li key={n}>
                          <button onClick={() => toggle(n)} data-testid={`sbp-step-${n}`}
                            className="w-full flex items-start gap-3 text-left group">
                            <span className={`mt-0.5 w-5 h-5 shrink-0 border flex items-center justify-center transition-colors ${isDone ? `${c.dot} border-transparent` : "border-white/25 group-hover:border-white/50"}`}>
                              {isDone && <Check size={13} className="text-navy" strokeWidth={3} />}
                            </span>
                            <span className="flex items-start gap-2">
                              <span className={`text-xs font-mono mt-0.5 ${isDone ? "text-gray-600" : "text-gray-500"}`}>{String(n).padStart(2, "0")}</span>
                              <span className={`text-sm leading-snug transition-colors ${isDone ? "text-gray-500 line-through" : "text-gray-200 group-hover:text-white"}`}>{label}</span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* ATF Can Help */}
                <div className={`m-4 mt-0 p-4 border ${c.soft}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <HelpIcon size={15} className={c.icon} />
                    <span className="text-xs font-semibold uppercase tracking-wide text-white">ATF Can Help</span>
                  </div>
                  <ul className="space-y-1 mb-3">
                    {p.help.map((h) => (
                      <li key={h} className="text-xs text-gray-400 flex items-center gap-1.5">
                        <span className={`w-1 h-1 rounded-full ${c.dot}`} /> {h}
                      </li>
                    ))}
                  </ul>
                  <Link to={p.helpTo} data-testid={`sbp-help-${p.key}`}
                    className={`inline-flex items-center gap-1 text-xs font-semibold ${c.icon} hover:opacity-80 transition-opacity`}>
                    {p.helpCta} <ArrowRight size={12} />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Additional + state resources */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="bg-white/[0.03] border border-white/10 p-6 md:p-8 grid lg:grid-cols-2 gap-10">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-gold mb-5">Additional Resources</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {RESOURCES.map((r) => {
                const RIcon = r.icon;
                return (
                  <Link key={r.title} to={r.to} data-testid={`sbp-resource-${r.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="flex items-center gap-3 border border-white/10 hover:border-gold/40 hover:bg-white/[0.03] transition-colors px-4 py-3">
                    <RIcon size={18} className="text-gold shrink-0" />
                    <span className="text-sm text-gray-200">{r.title}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-gold mb-2">State-Specific Resources</h3>
            <p className="text-sm text-gray-400 mb-4">
              Requirements for registration, licenses, permits and taxes vary by state. Pick where you'll start your business to find official government resources.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <select value={state} onChange={(e) => setState(e.target.value)} data-testid="sbp-state-select"
                  className="w-full bg-navy border border-white/15 text-sm text-gray-200 pl-9 pr-3 py-3 appearance-none focus:border-gold/50 outline-none">
                  <option value="">Select your state…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-4 space-y-2" data-testid="sbp-state-links">
              <a href="https://www.sba.gov/business-guide" target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between border border-white/10 hover:border-gold/40 px-4 py-2.5 text-sm text-gray-200 transition-colors">
                <span>SBA Business Guide (Official)</span> <ExternalLink size={14} className="text-gold" />
              </a>
              <a href="https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between border border-white/10 hover:border-gold/40 px-4 py-2.5 text-sm text-gray-200 transition-colors">
                <span>Apply for your EIN (IRS)</span> <ExternalLink size={14} className="text-gold" />
              </a>
              {state && (
                <>
                  <a href={stateSearch("register a business Secretary of State")} target="_blank" rel="noopener noreferrer" data-testid="sbp-state-register"
                    className="flex items-center justify-between border border-white/10 hover:border-gold/40 px-4 py-2.5 text-sm text-gray-200 transition-colors">
                    <span>Register a business in {state}</span> <ExternalLink size={14} className="text-gold" />
                  </a>
                  <a href={stateSearch("business licenses and permits")} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-between border border-white/10 hover:border-gold/40 px-4 py-2.5 text-sm text-gray-200 transition-colors">
                    <span>{state} licenses & permits</span> <ExternalLink size={14} className="text-gold" />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
              <Handshake size={22} className="text-gold" />
            </div>
            <div>
              <div className="font-heading text-xl text-white">You don't have to do it alone.</div>
              <p className="text-sm text-gray-400">ATF WebWorks is here to help you every step of the way.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/start" data-testid="sbp-cta-assessment" className="bg-gold text-navy hover:bg-gold-light transition-colors px-6 py-3 text-sm font-semibold">
              Free Business Assessment
            </Link>
            <Link to="/book" data-testid="sbp-cta-consult" className="border border-white/20 text-white hover:bg-white/5 transition-colors px-6 py-3 text-sm font-semibold">
              Book a Free Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
